import React, { useCallback, useMemo } from 'react';
import { useGetCatalogProductRatingQuery } from '@graphql/generated';
import { Box, BoxProps, Flex, IconV2, TextV2 } from '@withjoy/joykit';
import { times } from 'lodash-es';
import { useTranslation } from '@shared/core';
import { Star } from '@apps/registry/common/components/Star';
import { StyledStartContainer } from './ProductRating.styles';

type RatingLabelProps = {
  rating: number;
  totalReviewCount: number;
  scrollToElementId?: string;
  /**
   * @default 'averageRatingWithCount'
   */
  singleLine?: boolean;
  labelVariant?: 'noLabel' | 'averageRating' | 'averageRatingWithCount' | 'averageRatingWithCountShort' | 'totalReviewCount' | 'totalReviewCountShort';
};

const numberFormatter = new Intl.NumberFormat('en-us');

const RatingLabel = (props: RatingLabelProps) => {
  const { rating, totalReviewCount, scrollToElementId, labelVariant = 'averageRatingWithCount', singleLine = false } = props;

  const formattedTotalReviewCount = useMemo(() => numberFormatter.format(totalReviewCount), [totalReviewCount]);

  const { t } = useTranslation('catalogRegistry');
  const text = t('productRating', labelVariant)({ averageRating: rating, totalReviewCount: formattedTotalReviewCount });
  const overflowElided: Partial<Pick<BoxProps, 'whiteSpace' | 'overflow' | 'textOverflow'>> = singleLine
    ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
    : {};

  return scrollToElementId ? (
    <Box
      as={'a'}
      href={`#${scrollToElementId}`}
      onClick={e => {
        e.preventDefault();
        document.getElementById(scrollToElementId)?.scrollIntoView({
          behavior: 'instant',
        });
      }}
      typographyVariant={['label1', null, 'body1']}
      overflow={overflowElided.overflow}
      borderBottom="1px solid transparent"
      _groupHover={{
        borderColor: 'mono5',
      }}
      _pressed={{
        borderColor: 'mono5',
      }}
    >
      {text}
    </Box>
  ) : (
    <TextV2
      typographyVariant={['label1', null, 'body1']}
      {...overflowElided}
    >
      {text}
    </TextV2>
  );
};

export type StarRatingProps = {
  rating: number;

  /**
   * Star size
   *
   * @default 'md'
   */
  iconSize?: number | 'sm' | 'md';
  singleLine?: boolean;
  showPartialRating?: boolean;
  scrollToElementId?: string;
} & BoxProps;

const TOTAL_STARS = 5;

export type StarRatingShimProps = StarRatingProps & Pick<StarRatingWithLabelProps, 'labelVariant'>;
export const StarRatingShim = ({ children, iconSize = 'md', labelVariant, ...restProps }: StarRatingShimProps) => {
  // is always a `singleLine`
  const noLabel = labelVariant === 'noLabel';
  return (
    <StyledStartContainer
      role="group"
      {...restProps}
    >
      <Flex>
        <IconV2 size={iconSize} />
        {noLabel ? null : (
          <TextV2
            typographyVariant={['label1', null, 'body1']}
            opacity={0}
            dangerouslySetInnerHTML={{ __html: '&nbsp;' }}
          />
        )}
      </Flex>
      {children}
    </StyledStartContainer>
  );
};

export const StarRating = React.memo<StarRatingProps>(({ children, rating, iconSize = 'md', showPartialRating = false, id, scrollToElementId, ...restProps }) => {
  const getFilledFraction = useCallback(
    (idx: number) => {
      const difference = rating - idx;
      if (difference > 1 || (difference > 0 && !showPartialRating)) {
        return 1;
      } else if (difference < 0) {
        return 0;
      }
      return difference;
    },
    [rating, showPartialRating],
  );

  const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    if (scrollToElementId) {
      document.getElementById(scrollToElementId)?.scrollIntoView({
        behavior: 'instant',
      });
    }
  };

  return (
    <StyledStartContainer
      columnGap={4}
      onClick={handleOnClick}
      role="group"
      scrollToElementId={scrollToElementId}
      {...restProps}
    >
      <Flex
        columnGap={1}
        justifyContent="flex-end"
      >
        {times(TOTAL_STARS, idx => {
          return (
            <Star
              key={`${id}-${idx}`}
              id={`${id}-${idx}`}
              fillColor={'mono14'}
              bgColor={'mono5'}
              iconSize={iconSize}
              colorFilledFraction={getFilledFraction(idx)}
            />
          );
        })}
      </Flex>
      {children}
    </StyledStartContainer>
  );
});

type StarRatingWithLabelProps = StarRatingProps & {
  rating: number;
  totalReviewCount: number;
  scrollToElementId?: string;
  labelVariant?: RatingLabelProps['labelVariant'];
} & BoxProps;

export const StarRatingWithLabel = ({ rating, totalReviewCount, scrollToElementId, singleLine, labelVariant, ...restProps }: StarRatingWithLabelProps) => {
  const noLabel = labelVariant === 'noLabel';
  return (
    <StarRating
      rating={rating}
      scrollToElementId={scrollToElementId}
      {...restProps}
    >
      {noLabel ? null : (
        <RatingLabel
          labelVariant={labelVariant}
          rating={rating}
          totalReviewCount={totalReviewCount}
          scrollToElementId={scrollToElementId}
          singleLine={singleLine}
        />
      )}
    </StarRating>
  );
};

type ProductRatingProps = {
  catalogItemId: string;
  /**
   * When provided, this component becomes clickable and will scroll to the target element.
   */
  scrollToElementId?: string;
} & BoxProps;

export const ProductRating = (props: ProductRatingProps) => {
  const { catalogItemId, ...restProps } = props;
  const { data } = useGetCatalogProductRatingQuery({
    variables: {
      catalogItemId,
    },
    batchMode: 'fast',
  });

  return data?.productCatalogItemById?.rating?.stats ? (
    <StarRatingWithLabel
      rating={data.productCatalogItemById.rating.stats.averageRating}
      totalReviewCount={data.productCatalogItemById.rating.stats.totalReviewCount}
      {...restProps}
    />
  ) : null;
};
